import { connect } from 'react-redux';
import { get, keyBy, sortBy } from 'lodash';
import { unstable_batchedUpdates } from 'react-dom';
import i18next from 'i18next';
import moment from 'moment-timezone';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Box } from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';

/** ACTIONS */
import { closeGenericModal, openGenericModal, refreshGenericModal } from '@actions/modal';
import { loading, loadingSuccess } from '@actions/loading';
import { showErrorMessage, showMessage, showSuccessMessage } from '@actions/messageconfirmation';

/** COMMONS */
import { Button, Dropdown } from '@commons/utils/styledLibraryComponents';
import {
  DATAGRID_LOCALES,
  DATAGRID_PAGE_SIZE_OPTIONS,
  generateDates,
  handleCellClick,
  handleCellEditStop,
} from '@commons/utils/dataGrid';
import { DataGridLoadingOverlay } from '@commons/DataGridLoadingOverlay';
import { DATE_DISPLAY_FORMATS } from '@commons/DatePickers/constants';
import {
  GenericContentContainer,
  GenericGapContainer,
  GenericPageContainer,
} from '@commons/Layout/styledComponents';
import { getClientStoreNameTranslation } from '@commons/utils/translations';
import { getTimeSlotsToUse } from '@productions/utils/commons';
import { getUserTimezone } from '@commons/utils/date';
import { IMPORT_MODAL_STEPS } from '@commons/Modals/MultiStepModal';
import { LeftRightSplitter } from '@commons/LeftRightSplitter';
import { PeriodDatePicker } from '@commons/DatePickers/PeriodDatePicker';
import { TabPanel } from '@commons/TabPanel';
import { usePeriodDatePickerState } from '@hooks/usePeriodDatePickerState';
import Drawer from '@commons/Drawer';
import KpiDisplay from '@commons/KpiDisplay';
import NavigationBreadCrumb from '@commons/Breadcrumb/NavigationBreadCrumb';

/** SELECTORS */
import { canEditSalesPlanning } from '@selectors/actions/salesPlanningActions';
import { getAuthorizedActions, getPrevHorizon } from '@selectors/featureProps';
import { getClientInfo } from '@selectors/client';
import { getSalesPointStores } from '@selectors/stores';

/** UTILS */
import { downloadFile, formatXlsFileToJson } from '@backoffice/utils';
import { exportSalesPlanning } from './utils/export';
import {
  formatProductForecastBatchUpdateFile,
  getImportProductForecastModalConfig,
} from './utils/import';
import { getActions } from './utils/getActions';
import { getColumns } from './utils/getColumns';
import {
  getOverwriteProductionEntriesWarningModalParams,
  getProductForecastTemplateModalConfig,
  getResetWarningModalParams,
} from './utils/modals';
import salesPlanningUtils from './utils';

/** SERVICES */
import { forecast as forecastService } from '@services/forecast';
import { product as productService } from '@services/product';
import productionPlanningService from '@services/productionPlanning';
import storeService from '@services/store';

import {
  COLUMN_TYPES,
  COLUMNS,
  DATE_CHANGE,
  getPendingCellKey,
  PENDING_CELL_CLASSNAME,
  VIEW_MODES,
} from './datagrid/constants';
import { COMMON_DATAGRID_PROPS, DATAGRID_SLOTS } from './datagrid/datagridProps';

import { isCellEditable } from './datagrid/customCellRenders';
import { SalesPlanningDatagridToolbar } from './datagrid/SalesPlanningDatagridToolbar';
import { SalesPlanningTabs } from './components/SalesPlanningTabs';

import DetailedPdfConfig from './DetailedPdfConfig';
import PdfConfig from './PdfConfig';

import { StyledDataGridContainer } from './styledComponents';

const PERIOD_NB_DAYS_DEFAULT = 7;

/**
 * Applies the quantity deltas returned by the forecast upsert to the recipes/ingredients data.
 * Written as a pure function usable in a functional setState so that concurrent in-flight
 * edits never compute from a stale snapshot, and touched rows get new references
 * (the datagrid relies on reference changes to re-render them).
 */
const applyEntityQuantityDeltas = (entitiesData, entityDeltas, modifiedDate) => {
  const updatedEntitiesData = { ...entitiesData };

  for (const entityId in entityDeltas) {
    const entityQuantity = entityDeltas[entityId].quantity;
    const currentEntityData = updatedEntitiesData[entityId];

    // Entity unknown to the loaded data (e.g. composition changed since the page loaded):
    // skip it — throwing here would crash the render since this runs in a setState updater
    if (!currentEntityData) {
      continue;
    }

    updatedEntitiesData[entityId] = {
      ...currentEntityData,
      [modifiedDate]: (currentEntityData[modifiedDate] || 0) + entityQuantity,
      total: currentEntityData.total + entityQuantity,
    };
  }

  return updatedEntitiesData;
};

/**
 * Same as applyEntityQuantityDeltas but for the day view, where quantities live under
 * dataByTimeSlotIds[productionTimeSlotId].toProduce.
 */
const applyTimeSlotProductionDeltas = (entitiesData, entityDeltas, productionTimeSlotId) => {
  const updatedEntitiesData = { ...entitiesData };

  for (const entityId in entityDeltas) {
    const entityQuantity = entityDeltas[entityId].quantity;
    const currentEntityData = updatedEntitiesData[entityId];

    // Entity unknown to the loaded data (e.g. composition changed since the page loaded):
    // skip it — throwing here would crash the render since this runs in a setState updater
    if (!currentEntityData) {
      continue;
    }

    const currentTimeSlotData = currentEntityData.dataByTimeSlotIds?.[productionTimeSlotId] || {};

    updatedEntitiesData[entityId] = {
      ...currentEntityData,
      dataByTimeSlotIds: {
        ...currentEntityData.dataByTimeSlotIds,
        [productionTimeSlotId]: {
          ...currentTimeSlotData,
          toProduce: (currentTimeSlotData.toProduce || 0) + entityQuantity,
        },
      },
    };
  }

  return updatedEntitiesData;
};

const SalesPlanning = (props) => {
  const {
    match,
    prevHorizon,
    client: { defaultTimezone, useTtc, storeName },
    user,
    stores,
    currency,
    showErrorMessage,
    showSuccessMessage,
    showMessage,
    openGenericModal,
    refreshGenericModal,
    closeGenericModal,
    pageLoading,
    pageLoaded,
    authorizedActions,
  } = props;

  const USER_TIMEZONE = getUserTimezone();
  const TODAY = moment.tz(USER_TIMEZONE);

  const prevHorizonDate = moment.tz(USER_TIMEZONE).add(prevHorizon.future, 'days');

  /*******************
   ***** STATES *****
   *******************/

  const [isLoading, setIsLoading] = useState(false);
  const [actions, setActions] = useState();

  // File import states
  const [templateModalStores] = useState(stores || []);
  const [templateSelectedStores, setTemplateSelectedStores] = useState(stores || []);
  const [templateCategories, setTemplateCategories] = useState([]);
  const [templateSelectedCategories, setTemplateSelectedCategories] = useState([]);
  const [templatePeriod, setTemplatePeriod] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);

  // Header
  const [selectedStore, setSelectedStore] = useState(stores[0]);
  const [categories, setCategories] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const periodPickerState = usePeriodDatePickerState(
    TODAY,
    moment(TODAY).add(PERIOD_NB_DAYS_DEFAULT, 'days'),
    defaultTimezone,
  );
  const [maxFutureDate, setMaxFutureDate] = useState(null);

  const [displayPDFconfig, setDisplayPDFconfig] = useState(false);
  const [displayDetailedPDFConfig, setDisplayDetailedPDFConfig] = useState(false);

  // Tabs management
  const [tabValue, setTabValue] = useState(0);

  // Datagrid view mode
  const [viewMode, setViewMode] = useState(VIEW_MODES.CALENDAR);

  // Datagrid selected date
  const [selectedDate, setSelectedDate] = useState(TODAY);

  // Datagrid Columns
  const [productsColumns, setProductsColumns] = useState([]);
  const [recipesColumns, setRecipesColumns] = useState([]);
  const [ingredientsColumns, setIngredientsColumns] = useState([]);

  // Time slots
  const [timeSlots, setTimeSlots] = useState([]);

  const [timeSlotsGroupedByDate, setTimeSlotsGroupedByDate] = useState({});

  // Cell editing state for touch devices
  const [cellModesModel, setCellModesModel] = useState({});

  // Cells with an in-flight upsert, keyed by `${rowId}|${field}` (saving indicator)
  const [pendingCellKeys, setPendingCellKeys] = useState(() => new Set());

  const addPendingCellKey = useCallback((cellKey) => {
    setPendingCellKeys((previousKeys) => {
      const updatedKeys = new Set(previousKeys);

      updatedKeys.add(cellKey);

      return updatedKeys;
    });
  }, []);

  const removePendingCellKey = useCallback((cellKey) => {
    setPendingCellKeys((previousKeys) => {
      const updatedKeys = new Set(previousKeys);

      updatedKeys.delete(cellKey);

      return updatedKeys;
    });
  }, []);

  const getProductCellClassName = useCallback(
    ({ id, field }) => {
      if (pendingCellKeys.has(getPendingCellKey(id, field))) {
        return PENDING_CELL_CLASSNAME;
      }

      return '';
    },
    [pendingCellKeys],
  );

  /**
   * While an upsert is in flight, the controls that replace the grid dataset (store,
   * categories, period, day navigation, view mode) must stay locked: a late response
   * would otherwise apply its deltas to the freshly fetched data and corrupt it.
   */
  const hasPendingCells = pendingCellKeys.size > 0;

  // Datagrid pagination - separate for each datagrid for independent pagination
  const [productsPaginationModel, setProductsPaginationModel] = useState({
    page: 0,
    pageSize: 50,
  });
  const [recipesPaginationModel, setRecipesPaginationModel] = useState({
    page: 0,
    pageSize: 50,
  });
  const [ingredientsPaginationModel, setIngredientsPaginationModel] = useState({
    page: 0,
    pageSize: 50,
  });

  // Keep an array corresponding to the selected period for the DataGrid's columns
  const selectedDates = useMemo(() => {
    if (!periodPickerState.startDate || !periodPickerState.endDate) {
      return [];
    }

    return generateDates(periodPickerState.startDate, periodPickerState.endDate);
  }, [periodPickerState.startDate, periodPickerState.endDate]);

  // Update selectedDate when period changes (moved out of useMemo to avoid side effects)
  useEffect(() => {
    if (periodPickerState.startDate) {
      setSelectedDate(periodPickerState.startDate);
    }
  }, [periodPickerState.startDate]);

  /**
   * Each tab will have its own data, rows and columns (although columns are identical between recipes and ingredients)
   */
  const [productsData, setProductsData] = useState({});
  const [recipesData, setRecipesData] = useState({});
  const [ingredientsData, setIngredientsData] = useState({});

  const productsRows = useMemo(() => Object.values(productsData), [productsData]);
  const recipesRows = useMemo(() => Object.values(recipesData), [recipesData]);
  const ingredientsRows = useMemo(() => Object.values(ingredientsData), [ingredientsData]);

  const [productsDataWithTimeSlots, setProductsDataWithTimeSlots] = useState({});
  const [recipesDataWithTimeSlots, setRecipesDataWithTimeSlots] = useState({});
  const [ingredientsDataWithTimeSlots, setIngredientsDataWithTimeSlots] = useState({});

  const productsRowsWithTimeSlots = useMemo(() => {
    const productRowsByProductId = keyBy(productsRows, 'id');

    return salesPlanningUtils.getFormattedDataWithTimeSlots(
      productRowsByProductId,
      productsDataWithTimeSlots,
      selectedDate,
      true,
    );
  }, [productsDataWithTimeSlots, productsRows, selectedDate]);

  const recipesRowsWithTimeSlots = useMemo(() => {
    const productRowsByProductId = keyBy(productsRows, 'id');

    return salesPlanningUtils.getFormattedDataWithTimeSlots(
      productRowsByProductId,
      recipesDataWithTimeSlots,
      selectedDate,
      false,
    );
  }, [recipesDataWithTimeSlots, productsRows, selectedDate]);

  const ingredientsRowsWithTimeSlots = useMemo(() => {
    const productRowsByProductId = keyBy(productsRows, 'id');

    return salesPlanningUtils.getFormattedDataWithTimeSlots(
      productRowsByProductId,
      ingredientsDataWithTimeSlots,
      selectedDate,
      false,
    );
  }, [ingredientsDataWithTimeSlots, productsRows, selectedDate]);

  const [combinedPastAndForecastTurnover, setCombinedPastAndForecastTurnover] = useState({});

  // Track when changes have been made (allows to trigger KPI update after either filters or datagrid changes)
  const [hasDataChanges, setHasDataChanges] = useState(false);

  const handleZoomClick = useCallback((colDef) => {
    const clickedDate = colDef.field; // The field contains the date string

    // Switch to DAY mode
    setViewMode(VIEW_MODES.DAY);

    // Set the selected date to the clicked column's date
    setSelectedDate(moment(clickedDate).toDate());
  }, []);

  /*******************
   ***** EFFECTS *****
   *******************/

  // Fetch categories
  useEffect(() => {
    (async () => {
      const { categories } = await productService.getProductsCategories();

      const formattedCategories = categories.map((category) => ({
        ...category,
        name: category.name || i18next.t('GENERAL.NONE_VALUE'),
      }));

      setCategories(formattedCategories);
      setSelectedCategories(formattedCategories);
      setViewMode(VIEW_MODES.CALENDAR);

      setTemplateCategories(formattedCategories);
      setTemplateSelectedCategories(formattedCategories);
    })();
  }, [selectedStore]);

  // Max selectable period is 2 weeks, taking max future prev horizon into account
  useEffect(() => {
    const maxEndDate = moment.tz(periodPickerState.startDate, USER_TIMEZONE).add(2, 'weeks');

    setMaxFutureDate(moment.min(prevHorizonDate, maxEndDate));
  }, [periodPickerState.startDate]);

  // Updates the columns when the selected dates change
  useEffect(() => {
    if (!selectedDates.length) {
      return;
    }

    setProductsColumns(
      getColumns(COLUMN_TYPES.PRODUCTS, selectedDates, {
        onZoomClick: handleZoomClick,
        timeSlotsGroupedByDate,
        viewMode,
        selectedDate,
        productionTimeSlots: timeSlots,
        onRefreshClick: handleRefreshIconClick,
        canEditSalesPlanning: canEditSalesPlanning(authorizedActions),
      }),
    );
    setRecipesColumns(
      getColumns(COLUMN_TYPES.RECIPES, selectedDates, {
        onZoomClick: handleZoomClick,
        timeSlotsGroupedByDate,
        viewMode,
        selectedDate,
        productionTimeSlots: timeSlots,
      }),
    );
    setIngredientsColumns(
      getColumns(COLUMN_TYPES.INGREDIENTS, selectedDates, {
        onZoomClick: handleZoomClick,
        timeSlotsGroupedByDate,
        viewMode,
        selectedDate,
        productionTimeSlots: timeSlots,
      }),
    );
  }, [selectedDates, viewMode, timeSlots, timeSlotsGroupedByDate]);

  // Fetch Sales Planning data on store, period and categories change
  useEffect(() => {
    if (
      !selectedStore ||
      !periodPickerState.startDate ||
      !periodPickerState.endDate ||
      !selectedCategories.length ||
      viewMode !== VIEW_MODES.CALENDAR
    ) {
      return;
    }

    (async () => {
      await getAggregatedProductForecast();

      // Mark that changes have been made to trigger KPI update after filters update
      setHasDataChanges(true);
    })();
  }, [
    selectedStore,
    periodPickerState.startDate,
    periodPickerState.endDate,
    selectedCategories,
    viewMode,
  ]);

  // Update KPI after DataGrid has been updated following data changes, either from user interaction or filter changes
  useEffect(() => {
    if (hasDataChanges) {
      (async () => {
        try {
          await updateTurnoverKpi();
        } catch {
          showErrorMessage(i18next.t('FORECAST.SALES_PLANNING.ERROR_FETCHING_TURNOVER'));
        } finally {
          setHasDataChanges(false);
        }
      })();
    }
  }, [hasDataChanges]);

  useEffect(() => {
    setActions(
      getActions({
        handleXLSExport,
        setDisplayPDFconfig,
        setDisplayDetailedPDFConfig,
        openProductForecastTemplateDownloadModal,
        openProductForecastBatchImportModal,
        authorizedActions,
      }),
    );
  }, [recipesRows, ingredientsRows, productsRows]);

  // Fetch Sales Planning data with time slots on store, view mode, selected date and categories change
  useEffect(() => {
    if (
      !selectedStore ||
      !selectedDate ||
      !selectedCategories.length ||
      viewMode !== VIEW_MODES.DAY
    ) {
      return;
    }

    (async () => {
      await getProductionEntries();

      // Mark that changes have been made to trigger KPI update after filters update
      setHasDataChanges(true);
    })();
  }, [viewMode, selectedStore, selectedDate, selectedCategories]);

  useEffect(() => {
    if (!templateSelectedStores) {
      return;
    }

    openProductForecastTemplateDownloadModal(true);
  }, [templateSelectedStores, templateSelectedCategories, templatePeriod]);

  /*******************
   ***** METHODS *****
   *******************/
  const updateTurnoverKpi = async () => {
    const startDate = moment(periodPickerState.startDate).format(
      DATE_DISPLAY_FORMATS.DASHED_YEAR_MONTH_DAY,
    );
    const endDate = moment(periodPickerState.endDate).format(
      DATE_DISPLAY_FORMATS.DASHED_YEAR_MONTH_DAY,
    );
    const selectedCategoryIds = selectedCategories.map(({ id }) => id);

    const combinedPastAndForecastTurnover =
      await forecastService.getCombinedPastAndForecastTurnover(
        selectedStore.id,
        startDate,
        endDate,
        selectedCategoryIds,
      );

    setCombinedPastAndForecastTurnover(combinedPastAndForecastTurnover);
  };

  const getValidStoreTimeSlotsForPeriod = async () => {
    const timeSlotsGroupedByDate = await storeService.getValidStoreTimeSlots(selectedStore.id, {
      startDate: moment(periodPickerState.startDate).format(
        DATE_DISPLAY_FORMATS.DASHED_YEAR_MONTH_DAY,
      ),
      endDate: moment(periodPickerState.endDate).format(DATE_DISPLAY_FORMATS.DASHED_YEAR_MONTH_DAY),
    });

    return timeSlotsGroupedByDate;
  };

  const getAggregatedProductForecast = async () => {
    setIsLoading(true);

    try {
      const startDate = moment(periodPickerState.startDate).format(
        DATE_DISPLAY_FORMATS.DASHED_YEAR_MONTH_DAY,
      );
      const endDate = moment(periodPickerState.endDate).format(
        DATE_DISPLAY_FORMATS.DASHED_YEAR_MONTH_DAY,
      );

      const selectedCategoryIds = selectedCategories.map(({ id }) => id);

      const data = await productionPlanningService.getAggregatedProductForecast(selectedStore.id, {
        startDate,
        endDate,
        categoryIds: selectedCategoryIds,
        includeProductionMininum: true,
      });

      // Use "getValidStoreTimeSlotsForPeriod" only if we are in calendar view mode
      const timeSlotsGroupedByDate = await getValidStoreTimeSlotsForPeriod();
      const timeSlotsOfTheStartDate = timeSlotsGroupedByDate[startDate] || [];

      setTimeSlots(timeSlotsOfTheStartDate); // The Toolbar will display the view mode toggle if there are time slots on the first day
      setTimeSlotsGroupedByDate(timeSlotsGroupedByDate);

      setProductsColumns(
        getColumns(COLUMN_TYPES.PRODUCTS, selectedDates, {
          onZoomClick: handleZoomClick,
          timeSlotsGroupedByDate,
          viewMode,
          selectedDate,
          productionTimeSlots: timeSlotsOfTheStartDate,
          onRefreshClick: handleRefreshIconClick,
          canEditSalesPlanning: canEditSalesPlanning(authorizedActions),
        }),
      );
      setProductsData(data.products);
      setRecipesData(data.recipes);
      setIngredientsData(data.ingredients);
    } catch {
      setProductsData({});
      setRecipesData({});
      setIngredientsData({});
      setCombinedPastAndForecastTurnover({});
      showErrorMessage(i18next.t('FORECAST.POS_PRODUCTION_PLANNING.ERROR_FETCHING_DATA'));
    } finally {
      setIsLoading(false);
    }
  };

  const fetchProductionEntriesAndSetTimeSlotsToUse = async () => {
    const productionEntriesDate = moment(selectedDate).format(
      DATE_DISPLAY_FORMATS.DASHED_YEAR_MONTH_DAY,
    );

    const selectedCategoryIds = selectedCategories.map(({ id }) => id);

    const { products, recipes, ingredients } = await productionPlanningService.getProductionEntries(
      selectedStore.id,
      productionEntriesDate,
      { categoryIds: selectedCategoryIds, noOperatorForecasts: true },
    );

    // Only getProductionEntries service has dataByTimeSlotIds field in the response
    const { productTimeSlotIdsSet } = Object.values(products).reduce(
      (acc, product) => {
        Object.keys(product.dataByTimeSlotIds).forEach((timeSlotId) => {
          acc.productTimeSlotIdsSet.add(timeSlotId);
        });

        return acc;
      },
      { productTimeSlotIdsSet: new Set() },
    );

    const timeSlotsToUse = getTimeSlotsToUse(productTimeSlotIdsSet, {
      selectedDate,
      selectedStore,
    });

    setTimeSlots(timeSlotsToUse);

    return { products, recipes, ingredients, timeSlotsToUse };
  };

  const getProductionEntries = async () => {
    setIsLoading(true);

    try {
      const { products, recipes, ingredients, timeSlotsToUse } =
        await fetchProductionEntriesAndSetTimeSlotsToUse();

      setProductsColumns(
        getColumns(COLUMN_TYPES.PRODUCTS, selectedDates, {
          onZoomClick: handleZoomClick,
          timeSlotsGroupedByDate,
          viewMode,
          selectedDate,
          productionTimeSlots: timeSlotsToUse,
          onRefreshClick: handleRefreshIconClick,
          canEditSalesPlanning: canEditSalesPlanning(authorizedActions),
        }),
      );

      setProductsDataWithTimeSlots(products);
      setRecipesDataWithTimeSlots(recipes);
      setIngredientsDataWithTimeSlots(ingredients);
    } catch {
      setProductsDataWithTimeSlots({});
      setRecipesDataWithTimeSlots({});
      setIngredientsDataWithTimeSlots({});
      setCombinedPastAndForecastTurnover({});
      showErrorMessage(i18next.t('FORECAST.POS_PRODUCTION_PLANNING.ERROR_FETCHING_DATA'));
    } finally {
      setIsLoading(false);
    }
  };

  const handlePostAdminProductForecast = async (
    storeId,
    productId,
    modifiedDate,
    updatedQuantity,
    delta,
  ) => {
    const pendingCellKey = getPendingCellKey(productId, modifiedDate);

    addPendingCellKey(pendingCellKey);

    try {
      // dynamicTot will only be defined if updatedQuantity is null (i.e. the user has deleted admin product forecast)
      const { recipes, ingredients, dynamicTot } =
        await productionPlanningService.postAdminProductForecast(storeId, productId, {
          date: modifiedDate,
          quantity: updatedQuantity,
          delta,
        });

      // React 17 does not batch setState calls in async continuations: without batching,
      // each call below triggers its own full re-render of the page (COR-1576)
      unstable_batchedUpdates(() => {
        removePendingCellKey(pendingCellKey);
        setRecipesData((previousRecipesData) =>
          applyEntityQuantityDeltas(previousRecipesData, recipes, modifiedDate),
        );
        setIngredientsData((previousIngredientsData) =>
          applyEntityQuantityDeltas(previousIngredientsData, ingredients, modifiedDate),
        );
      });

      return dynamicTot;
    } catch (error) {
      removePendingCellKey(pendingCellKey);
      showErrorMessage(i18next.t('FORECAST.POS_PRODUCTION_PLANNING.ERROR_UPSERTING_DATA'));
    }
  };

  const handlePostProductionEntry = async (
    storeId,
    productId,
    selectedDate,
    productionTimeSlotId,
    adminForecast,
  ) => {
    const pendingCellKey = getPendingCellKey(productId, productionTimeSlotId);

    addPendingCellKey(pendingCellKey);

    try {
      const { recipes, ingredients } = await productionPlanningService.postProductionEntry(
        storeId,
        moment(selectedDate).format(DATE_DISPLAY_FORMATS.DASHED_YEAR_MONTH_DAY),
        productId,
        productionTimeSlotId,
        { adminForecast, noOperatorForecasts: true },
      );

      // React 17 does not batch setState calls in async continuations: without batching,
      // each call below triggers its own full re-render of the page (COR-1576)
      unstable_batchedUpdates(() => {
        removePendingCellKey(pendingCellKey);
        setRecipesDataWithTimeSlots((previousRecipesData) =>
          applyTimeSlotProductionDeltas(previousRecipesData, recipes, productionTimeSlotId),
        );
        setIngredientsDataWithTimeSlots((previousIngredientsData) =>
          applyTimeSlotProductionDeltas(previousIngredientsData, ingredients, productionTimeSlotId),
        );
      });

      return;
    } catch (error) {
      removePendingCellKey(pendingCellKey);
      showErrorMessage(i18next.t('FORECAST.POS_PRODUCTION_PLANNING.ERROR_UPSERTING_DATA'));
    }
  };

  const handleProcessRowUpdate = async (updatedRow, originalRow) => {
    // Need to do a deep copy for the catch block to work properly
    const originalRowClone = { ...originalRow };

    const modifiedDate = selectedDates.find((date) => updatedRow[date] !== originalRow[date]);

    if (!modifiedDate) {
      return originalRow;
    }

    // pointer-events blocks the mouse on a pending cell but not keyboard navigation:
    // ignore commits on a cell whose upsert is still in flight to avoid double-applied deltas
    if (pendingCellKeys.has(getPendingCellKey(updatedRow.id, modifiedDate))) {
      return originalRow;
    }

    const updatedQuantity = updatedRow[modifiedDate];

    const cellIsPFSaved = updatedRow.adminProductForecastDates.includes(modifiedDate);

    if (updatedQuantity === null && !cellIsPFSaved) {
      // updatedQuantity === null => user has deleted the cell's content
      showMessage(
        i18next.t('FORECAST.POS_PRODUCTION_PLANNING.DELETE_DYNAMIC_FORECAST_ERROR'),
        'warning',
      );
      return originalRowClone;
    }

    const oldQuantity = originalRowClone[modifiedDate];

    const hasChanges = updatedQuantity !== oldQuantity;

    if (hasChanges) {
      const { notComputedProductionEntryDates } = updatedRow;

      // Check if admin has changed the quantity of a production entry on a specific time slot
      if (
        !!notComputedProductionEntryDates &&
        notComputedProductionEntryDates.includes(modifiedDate)
      ) {
        return handleAPFOverwrite({
          modifiedDate,
          updatedRow,
          originalRowClone,
          handleOverwrite: () =>
            // Allow the overwrite of related production entries to happen
            postAdminProductForecast({
              updatedRow,
              originalRowClone,
              modifiedDate,
              updatedQuantity,
              oldQuantity,
            }),
        });
      }

      return postAdminProductForecast({
        updatedRow,
        originalRowClone,
        modifiedDate,
        updatedQuantity,
        oldQuantity,
      });
    }

    return originalRowClone;
  };

  const postAdminProductForecast = async ({
    updatedRow,
    originalRowClone,
    modifiedDate,
    updatedQuantity,
    oldQuantity,
  }) => {
    const delta = Math.round(updatedQuantity - oldQuantity);

    try {
      const dynamicTot = await handlePostAdminProductForecast(
        selectedStore.id,
        updatedRow.id, // = productId
        modifiedDate,
        updatedQuantity,
        delta,
      );

      if (updatedQuantity === null) {
        updatedRow[modifiedDate] = dynamicTot;

        // Remove the modified date from the admin product forecast dates
        updatedRow.adminProductForecastDates = updatedRow.adminProductForecastDates.filter(
          (date) => date !== modifiedDate,
        );

        /**
         * Instead of simply adding the delta here we want to add back the 'dynamic' PF's quantity.
         * Ex:
         *  - If dynamicTot was 100 and user deleted saved of 10 => delta would be -10
         *    But, the dynamicTot should be added back to the delta to get the correct value, i.e 90
         *
         * Ex2:
         *  - If dynamicTot was 10, user deleted saved of 50 => delta would be -50
         *    But, the dynamicTot should be added back to the delta to get the correct value, i.e -40
         */
        updatedRow.total += delta + dynamicTot;
      } else {
        updatedRow[modifiedDate] = updatedQuantity;
        updatedRow.adminProductForecastDates.push(modifiedDate);
        updatedRow.total += delta;
      }

      // Mark that changes have been made to trigger KPI update after DataGrid update
      setHasDataChanges(true);

      return updatedRow;
    } catch {
      return originalRowClone;
    }
  };

  const handleProcessRowUpdateWithTimeSlots = async (updatedRow, originalRow) => {
    // Need to do a deep copy for the catch block to work properly
    const originalRowClone = { ...originalRow };

    const { id: productId } = updatedRow;

    const modifiedTimeSlotCell = timeSlots.find(({ id }) => updatedRow[id] !== originalRow[id]);

    if (!modifiedTimeSlotCell) {
      return originalRow;
    }

    // pointer-events blocks the mouse on a pending cell but not keyboard navigation:
    // ignore commits on a cell whose upsert is still in flight to avoid double-applied deltas
    if (pendingCellKeys.has(getPendingCellKey(productId, modifiedTimeSlotCell.id))) {
      return originalRow;
    }

    const updatedQuantity = updatedRow[modifiedTimeSlotCell.id];

    if (updatedQuantity === null) {
      return originalRow;
    }

    const oldQuantity = originalRowClone[modifiedTimeSlotCell.id];

    const hasChanges = updatedQuantity !== oldQuantity;

    if (hasChanges) {
      const delta = Math.round(updatedQuantity - oldQuantity);

      try {
        await handlePostProductionEntry(
          selectedStore.id,
          productId,
          selectedDate,
          modifiedTimeSlotCell.id,
          updatedQuantity, // adminForecast
        );

        if (updatedQuantity === null) {
          return;
        }

        updatedRow[modifiedTimeSlotCell.id] = updatedQuantity;
        updatedRow.hasAdminPF = true;
        updatedRow.total += delta;

        // Mark that changes have been made to trigger KPI update after DataGrid update
        setHasDataChanges(true);

        return updatedRow;
      } catch {
        return originalRowClone;
      }
    }

    return originalRowClone;
  };

  const handleResetProductionEntries = async ({ productId }) => {
    try {
      await productionPlanningService.resetProductionEntries(
        selectedStore.id,
        [productId],
        moment(selectedDate).format(DATE_DISPLAY_FORMATS.DASHED_YEAR_MONTH_DAY),
      );

      // Refresh production entries in order to get correct production entries values
      await getProductionEntries();

      // Refresh aggregated product forecast in order to get correct admin product forecast values
      await getAggregatedProductForecast();
    } catch {
      showErrorMessage(i18next.t('FORECAST.POS_PRODUCTION_PLANNING.ERROR_RESETING_DATA'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleAPFOverwrite = async ({
    modifiedDate,
    updatedRow,
    originalRowClone,
    handleOverwrite,
  }) => {
    const { name } = updatedRow;

    // Need to use Promise in order to wait the user to confirm or cancel the PE.quantities overwrite
    return new Promise((resolve) => {
      openGenericModal(
        getOverwriteProductionEntriesWarningModalParams({
          productName: name,
          formattedDate: moment(modifiedDate).format(
            DATE_DISPLAY_FORMATS.CONDENSED_SLASHED_DAY_MONTH,
          ),
          handleCancelOverwrite: () => {
            resolve(originalRowClone);
          },
          handleOverwrite: () => {
            resolve(handleOverwrite());
          },
        }),
      );
    });
  };

  /*******************
   ***** HANDLERS *****
   *******************/
  // Stable reference so the memoized SalesPlanningTabs never re-renders with the page
  const handleTabChange = useCallback((event, newValue) => {
    setTabValue(newValue);
  }, []);

  const handleCellModesModelChange = useCallback((newModel) => {
    setCellModesModel(newModel);
  }, []);

  const handleXLSExport = () => {
    exportSalesPlanning(
      sortBy(productsRows, [COLUMNS.CATEGORY, COLUMNS.NAME]),
      sortBy(recipesRows, [COLUMNS.CATEGORY, COLUMNS.NAME]),
      sortBy(ingredientsRows, [COLUMNS.CATEGORY, COLUMNS.NAME]),
      {
        selectedStore,
        selectedDates,
        userLanguageCode: get(user, 'lnkLanguageAccountrel.code', 'fr'),
      },
    );
  };

  const handleViewModeChange = (_, newValue) => {
    if (!newValue || hasPendingCells) {
      return;
    }

    setViewMode(newValue);
  };

  const handleDateChange = (direction) => {
    if (hasPendingCells) {
      return;
    }

    const newDate = new Date(selectedDate);

    if (direction === DATE_CHANGE.PREV) {
      newDate.setDate(newDate.getDate() - 1);
    } else {
      newDate.setDate(newDate.getDate() + 1);
    }

    setSelectedDate(newDate);
  };

  const handleRefreshIconClick = ({ row }) => {
    const { id, name } = row;

    openGenericModal(
      getResetWarningModalParams({
        handleReset: () => handleResetProductionEntries({ productId: id }),
        productName: name,
        formattedDate: moment(selectedDate).format(
          DATE_DISPLAY_FORMATS.CONDENSED_DAY_DATE_MONTH_YEAR,
        ),
      }),
    );
  };

  /*******************
  /***** IMPORT ******
  /*******************/

  const openProductForecastTemplateDownloadModal = (shouldRefreshModal = false) => {
    const params = getProductForecastTemplateModalConfig(
      {
        // Handle stores
        filteredStores: templateModalStores,
        selectedStores: templateSelectedStores,
        setSelectedStores: setTemplateSelectedStores,
        // Handle categories
        categories: templateCategories,
        selectedCategories: templateSelectedCategories,
        setSelectedCategories: setTemplateSelectedCategories,
        // Handle date period
        initialStartDate: TODAY,
        initialEndDate: prevHorizonDate,
        maxPastDate: TODAY,
        maxFutureDate: prevHorizonDate,
        setTemplatePeriod,
        // Miscellaneous
        defaultTimezone,
        clientStoreName: getClientStoreNameTranslation(storeName, true),
      },
      handleProductForecastTemplateDownload,
      closeGenericModal,
    );

    if (shouldRefreshModal) {
      refreshGenericModal(params);
      return;
    }

    openGenericModal(params);
  };

  const handleProductForecastTemplateDownload = async () => {
    if (!templateSelectedStores || !templateSelectedCategories) {
      return;
    }

    const storeIds = templateSelectedStores.map(({ id }) => id);

    const categoryIds = templateSelectedCategories.map(({ id }) => id);

    try {
      showSuccessMessage(
        i18next.t('FORECAST.SALES_PLANNING.PRODUCT_FORECAST_TEMPLATE_FILE_LOADING'),
      );

      const result = await productionPlanningService.downloadProductForecastTemplate({
        storeIds,
        categoryIds,
        startDate: moment(templatePeriod.startDate).format(
          DATE_DISPLAY_FORMATS.DASHED_YEAR_MONTH_DAY,
        ),
        endDate: moment(templatePeriod.endDate).format(DATE_DISPLAY_FORMATS.DASHED_YEAR_MONTH_DAY),
      });

      downloadFile(
        result,
        i18next.t('FORECAST.SALES_PLANNING.PRODUCT_FORECAST_TEMPLATE_FILE_NAME'),
        false,
        false,
      );
    } catch {
      showErrorMessage(
        i18next.t('FORECAST.SALES_PLANNING.PRODUCT_FORECAST_TEMPLATE_FILE_LOADING_ERROR'),
      );
    }
  };

  const openProductForecastBatchImportModal = () => {
    const params = getImportProductForecastModalConfig(
      IMPORT_MODAL_STEPS.SELECT_FILE,
      {
        updatedFile: null,
        selectedFile: null,
        handleFileChange: onImportProductForecastFileChange,
      },
      props,
    );

    openGenericModal(params);
  };

  const onImportProductForecastFileChange = (file) => {
    setSelectedFile(file);

    refreshGenericModal(
      getImportProductForecastModalConfig(IMPORT_MODAL_STEPS.VALIDATE_FILE, {
        updatedFile: file,
        selectedFile,
        handleFileChange: onImportProductForecastFileChange,
        handleFileValidation: handleProductForecastUpdateFileValidation,
      }),
    );
  };

  const handleProductForecastUpdateFileValidation = async (file) => {
    pageLoading();

    try {
      const productForecastUpdateFile = await formatXlsFileToJson(file, {
        defval: null, // Keep column even if cells are empty
        blankrows: false, // Ignore row entirely empty
      });

      const formattedProductForecastUpdateFile =
        formatProductForecastBatchUpdateFile(productForecastUpdateFile);

      await productionPlanningService.validateImportProductForecast(
        formattedProductForecastUpdateFile,
      );

      refreshGenericModal(
        getImportProductForecastModalConfig(IMPORT_MODAL_STEPS.VALIDATED_FILE, {
          updatedFile: file,
          handleFileImport: () => handleProductForecastUpdate(formattedProductForecastUpdateFile),
        }),
      );
    } catch (error) {
      if (!error.response) {
        showErrorMessage(
          i18next.t('FORECAST.SALES_PLANNING.PRODUCT_FORECAST_TEMPLATE_FILE_IMPORT_ERROR'),
        );

        closeGenericModal();
        return;
      }

      refreshGenericModal(
        getImportProductForecastModalConfig(IMPORT_MODAL_STEPS.ERROR_FILE, {
          updatedFile: file,
          errorFile: error.response.data,
          status: error.response.status,
          handleFileChange: onImportProductForecastFileChange,
          storeName: getClientStoreNameTranslation(storeName, true),
          exportErrors: (data) => downloadFile(data, 'error_product_forecast_update', true, false),
        }),
      );
    } finally {
      pageLoaded();
    }
  };

  const handleProductForecastUpdate = async (payload) => {
    pageLoading();

    try {
      await productionPlanningService.importProductForecast(payload);

      await getAggregatedProductForecast();

      setHasDataChanges(true);

      showSuccessMessage(
        i18next.t('FORECAST.SALES_PLANNING.PRODUCT_FORECAST_TEMPLATE_FILE_IMPORT_SUCCESS'),
      );
    } catch {
      showErrorMessage(
        i18next.t('FORECAST.SALES_PLANNING.PRODUCT_FORECAST_TEMPLATE_FILE_IMPORT_ERROR'),
      );
    } finally {
      pageLoaded();
      closeGenericModal();
    }
  };

  /*******************
   ***** OTHER *****
   *******************/

  const customToolbar = () => (
    <SalesPlanningDatagridToolbar
      enableModeChange={salesPlanningUtils.hasProductionTimeSlots(timeSlots)} // The view mode change is only available if store has production time slots
      handleDateChange={handleDateChange}
      handleViewModeChange={handleViewModeChange}
      periodPickerState={periodPickerState}
      selectedDate={selectedDate}
      timeSlotsGroupedByDate={timeSlotsGroupedByDate}
      viewMode={viewMode}
    />
  );

  return (
    <GenericPageContainer>
      <NavigationBreadCrumb featurePath={match.path} />
      <GenericContentContainer height="calc(100% - 40px)" overflow="hidden">
        <Box sx={{ paddingBottom: '24px' }}>
          <LeftRightSplitter
            left={
              <GenericGapContainer>
                <Dropdown
                  iconSrc="/images/inpulse/pin-black-small.svg"
                  isDisabled={isLoading || hasPendingCells}
                  items={stores}
                  searchPlaceholder={i18next.t('GENERAL.SEARCH')}
                  selectedItem={selectedStore}
                  isRequired
                  onSelectionChange={(value) => {
                    setViewMode(VIEW_MODES.CALENDAR);
                    setSelectedStore(value);
                  }}
                />
                <Dropdown
                  iconSrc="/images/inpulse/category-ipblack-small.svg"
                  isDisabled={isLoading || !categories.length || hasPendingCells}
                  isUniqueSelection={false}
                  items={categories}
                  searchPlaceholder={i18next.t('GENERAL.SEARCH')}
                  selectedItems={selectedCategories}
                  isRequired
                  onSelectionChange={setSelectedCategories}
                />
                <PeriodDatePicker
                  customStyle={{ marginRight: '24px' }} // add an additional space between filters and KPIs
                  disabled={isLoading || viewMode === VIEW_MODES.DAY || hasPendingCells}
                  endDate={periodPickerState.endDate}
                  focusedDateInput={periodPickerState.focusedDateInput}
                  maxFutureDate={
                    // Allows to re-select a date outside of two weeks range
                    !!periodPickerState.startDate && !!periodPickerState.endDate
                      ? prevHorizonDate
                      : maxFutureDate
                  }
                  setFocusedDateInput={periodPickerState.setFocusedDateInput}
                  startDate={periodPickerState.startDate}
                  timezone={defaultTimezone}
                  onDatesChange={periodPickerState.handleSelectedDates}
                />
                <KpiDisplay
                  currency={currency}
                  isLoading={hasDataChanges}
                  label={i18next.t(
                    useTtc ? 'GENERAL.TURNOVER_TAX_INCLUDED' : 'GENERAL.TURNOVER_EX_TAX',
                  )}
                  tooltip={i18next.t('FORECAST.SALES_PLANNING.TURNOVER_TOOLTIP', {
                    inc_or_ex_taxes: i18next.t(
                      useTtc ? 'GENERAL.INCL_TAXES' : 'GENERAL.EXCL_TAXES',
                    ),
                  })}
                  value={get(
                    combinedPastAndForecastTurnover,
                    useTtc ? 'turnover' : 'turnoverExcludingTaxes',
                    null,
                  )}
                  withoutDecimals={false}
                />
                <KpiDisplay
                  currency={currency}
                  isLoading={hasDataChanges}
                  label={i18next.t('GENERAL.METRIC_MARGIN_EX_TAX_WITHOUT_CURRENCY')}
                  tooltip={i18next.t('FORECAST.POS_PRODUCTION_PLANNING.MARGIN_TOOLTIP', {
                    inc_or_ex_taxes: i18next.t(
                      useTtc ? 'GENERAL.INCL_TAXES' : 'GENERAL.EXCL_TAXES',
                    ),
                  })}
                  value={get(combinedPastAndForecastTurnover, 'margin', null)}
                  withoutDecimals={false}
                />
              </GenericGapContainer>
            }
            right={
              <Button
                actions={actions}
                color={'inpulse-outline'}
                iconDropdown={'/images/inpulse/carret-black.svg'}
                iconOnLeft={false}
                label={i18next.t('GENERAL.ACTIONS')}
              />
            }
          />
        </Box>

        <SalesPlanningTabs value={tabValue} onChange={handleTabChange} />
        <TabPanel index={0} value={tabValue}>
          <StyledDataGridContainer>
            <DataGridPro
              cellModesModel={cellModesModel}
              columns={productsColumns}
              getCellClassName={getProductCellClassName}
              hideFooter={false}
              isCellEditable={(props) => {
                const {
                  field,
                  row: { launchDate, endDate },
                } = props;

                return viewMode === VIEW_MODES.CALENDAR
                  ? isCellEditable(launchDate, endDate, field)
                  : isCellEditable(launchDate, endDate, selectedDate);
              }}
              loading={isLoading}
              localeText={
                DATAGRID_LOCALES[user.lnkLanguageAccountrel.code].components.MuiDataGrid
                  .defaultProps.localeText
              }
              pageSizeOptions={DATAGRID_PAGE_SIZE_OPTIONS}
              paginationModel={productsPaginationModel}
              processRowUpdate={
                viewMode === VIEW_MODES.CALENDAR
                  ? handleProcessRowUpdate
                  : handleProcessRowUpdateWithTimeSlots
              }
              rows={viewMode === VIEW_MODES.CALENDAR ? productsRows : productsRowsWithTimeSlots}
              pagination
              onCellClick={(params, event) => {
                handleCellClick(params, event, setCellModesModel);
              }}
              onCellEditStop={(params) => handleCellEditStop(params, setCellModesModel)}
              onCellModesModelChange={handleCellModesModelChange}
              {...COMMON_DATAGRID_PROPS}
              slots={{
                ...DATAGRID_SLOTS,
                toolbar: customToolbar,
                loadingOverlay: DataGridLoadingOverlay,
              }}
              onPaginationModelChange={setProductsPaginationModel}
            />
          </StyledDataGridContainer>
        </TabPanel>
        <TabPanel index={1} value={tabValue}>
          <StyledDataGridContainer>
            <DataGridPro
              columns={recipesColumns}
              hideFooter={false}
              loading={isLoading}
              localeText={
                DATAGRID_LOCALES[user.lnkLanguageAccountrel.code].components.MuiDataGrid
                  .defaultProps.localeText
              }
              pageSizeOptions={DATAGRID_PAGE_SIZE_OPTIONS}
              paginationModel={recipesPaginationModel}
              rows={viewMode === VIEW_MODES.CALENDAR ? recipesRows : recipesRowsWithTimeSlots}
              pagination
              {...COMMON_DATAGRID_PROPS}
              slots={{
                ...DATAGRID_SLOTS,
                toolbar: customToolbar,
                loadingOverlay: DataGridLoadingOverlay,
              }}
              onPaginationModelChange={setRecipesPaginationModel}
            />
          </StyledDataGridContainer>
        </TabPanel>
        <TabPanel index={2} value={tabValue}>
          <StyledDataGridContainer>
            <DataGridPro
              columns={ingredientsColumns}
              hideFooter={false}
              loading={isLoading}
              localeText={
                DATAGRID_LOCALES[user.lnkLanguageAccountrel.code].components.MuiDataGrid
                  .defaultProps.localeText
              }
              pageSizeOptions={DATAGRID_PAGE_SIZE_OPTIONS}
              paginationModel={ingredientsPaginationModel}
              rows={
                viewMode === VIEW_MODES.CALENDAR ? ingredientsRows : ingredientsRowsWithTimeSlots
              }
              pagination
              {...COMMON_DATAGRID_PROPS}
              slots={{
                ...DATAGRID_SLOTS,
                toolbar: customToolbar,
                loadingOverlay: DataGridLoadingOverlay,
              }}
              onPaginationModelChange={setIngredientsPaginationModel}
            />
          </StyledDataGridContainer>
        </TabPanel>

        <Drawer
          isOpened={displayPDFconfig}
          overlayColor={'rgba(34, 35, 33, 0.64)'}
          overlayOpacity={0.3}
          onClick={() => {
            setDisplayPDFconfig(false);
          }}
        >
          <PdfConfig
            categories={selectedCategories}
            endDate={periodPickerState.endDate}
            selectedStore={selectedStore}
            startDate={periodPickerState.startDate}
            storeId={selectedStore.id}
            onClose={() => setDisplayPDFconfig(false)}
          />
        </Drawer>

        <Drawer
          isOpened={displayDetailedPDFConfig}
          overlayColor={'rgba(34, 35, 33, 0.64)'}
          overlayOpacity={0.3}
          onClick={() => {
            setDisplayDetailedPDFConfig(false);
          }}
        >
          {/* Keyed on the open state so every opening remounts the panel with default fields */}
          <DetailedPdfConfig
            categories={selectedCategories}
            key={displayDetailedPDFConfig}
            storeId={selectedStore.id}
            onClose={() => setDisplayDetailedPDFConfig(false)}
          />
        </Drawer>
      </GenericContentContainer>
    </GenericPageContainer>
  );
};

const mapStateToProps = (state) => ({
  client: getClientInfo(state.baseReducer.user),
  prevHorizon: getPrevHorizon(state.baseReducer.userRights),
  stores: getSalesPointStores(state.baseReducer.activeStores),
  user: state.baseReducer.user,
  currency: state.baseReducer.currency,
  authorizedActions: getAuthorizedActions(
    state.baseReducer.userRights,
    '/forecast/forecast/sales-planning',
  ),
});

const mapDispatchToProps = (dispatch) => ({
  openGenericModal: (params) => {
    dispatch(openGenericModal(params));
  },
  refreshGenericModal: (params) => {
    dispatch(refreshGenericModal(params));
  },
  closeGenericModal: () => {
    dispatch(closeGenericModal());
  },
  showMessage: (message, type) => {
    dispatch(showMessage(message, type));
  },
  showSuccessMessage: (message) => {
    dispatch(showSuccessMessage(message));
  },
  showErrorMessage: (message) => {
    dispatch(showErrorMessage(message));
  },
  pageLoading: () => {
    dispatch(loading());
  },
  pageLoaded: () => {
    dispatch(loadingSuccess());
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(SalesPlanning);
